import React, { useEffect, useRef, useState } from 'react';

interface ChartViewerProps {
  chartPath: string;
  title?: string;
  height?: number;
  className?: string;
}

const ChartViewer: React.FC<ChartViewerProps> = ({
  chartPath,
  title,
  height = 600,
  className = ''
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!chartPath || !containerRef.current) return;

    setLoading(true);
    setError(null);
    
    // Ensure path starts with / for public folder
    const fullPath = chartPath.startsWith('/') ? chartPath : `/${chartPath}`;
    
    // Fetch HTML content
    fetch(fullPath)
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        return response.text();
      })
      .then(html => {
        if (!containerRef.current) return;
        
        // Clear container
        containerRef.current.innerHTML = '';
        
        // Create a temporary container to parse HTML
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;
        
        // Extract body content
        const body = tempDiv.querySelector('body') || tempDiv;
        const plotlyDiv = body.querySelector('.plotly-graph-div');
        const scripts = Array.from(body.querySelectorAll('script'));
        
        if (!plotlyDiv) {
          throw new Error('Chart div not found in HTML file');
        }
        
        // Clone and append the plotly div
        const clonedDiv = plotlyDiv.cloneNode(true) as HTMLElement;
        clonedDiv.style.width = '100%';
        clonedDiv.style.height = `${height}px`;
        clonedDiv.style.minHeight = `${height}px`;
        containerRef.current.appendChild(clonedDiv);
        
        // Execute scripts sequentially
        let scriptIndex = 0;
        const executeNextScript = () => {
          if (scriptIndex >= scripts.length) {
            // All scripts executed
            setTimeout(() => {
              setLoading(false);
            }, 500);
            return;
          }
          
          const script = scripts[scriptIndex];
          const newScript = document.createElement('script');
          
          if (script.src) {
            // External script
            newScript.src = script.src;
            newScript.async = false;
            newScript.onload = () => {
              scriptIndex++;
              executeNextScript();
            };
            newScript.onerror = () => {
              console.warn('Script load error:', script.src);
              scriptIndex++;
              executeNextScript();
            };
            document.head.appendChild(newScript);
          } else {
            // Inline script
            const scriptContent = script.textContent || script.innerHTML;
            if (scriptContent.trim()) {
              try {
                // Use Function constructor instead of eval for better error handling
                const func = new Function(scriptContent);
                func();
              } catch (e) {
                console.warn('Script execution warning:', e);
              }
            }
            scriptIndex++;
            executeNextScript();
          }
        };
        
        // Start executing scripts
        executeNextScript();
      })
      .catch(err => {
        console.error('Error loading chart:', err);
        setError(err.message || 'Failed to load chart');
        setLoading(false);
      });
  }, [chartPath, height]);

  if (!chartPath) {
    return (
      <div className="text-center py-12 text-gray-500">
        Chart path not provided.
      </div>
    );
  }

  return (
    <div className={`chart-viewer ${className}`}>
      {title && (
        <h3 className="text-xl font-light text-gray-800 mb-4">{title}</h3>
      )}
      <div className="relative bg-white rounded-lg overflow-hidden border border-gray-200" style={{ minHeight: height }}>
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-50 z-10">
            <div className="text-center">
              <div className="inline-block w-8 h-8 border-3 border-gray-300 border-t-gray-800 rounded-full animate-spin mb-2"></div>
              <p className="text-sm text-gray-500">Loading chart...</p>
              <p className="text-xs text-gray-400 mt-1">Large files may take a moment</p>
            </div>
          </div>
        )}
        {error ? (
          <div className="text-center py-12 text-red-500">
            <p className="font-semibold mb-2">{error}</p>
            <p className="text-sm text-gray-500 mt-2">Path: {chartPath}</p>
            <p className="text-xs text-gray-400 mt-2">
              Try opening{' '}
              <a 
                href={chartPath.startsWith('/') ? chartPath : `/${chartPath}`} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="underline text-blue-500"
              >
                this link
              </a>
              {' '}directly in your browser
            </p>
            <p className="text-xs text-gray-400 mt-1">
              Check browser console (F12) for more details
            </p>
          </div>
        ) : (
          <div
            ref={containerRef}
            className="w-full"
            style={{ 
              minHeight: height,
              display: loading ? 'none' : 'block'
            }}
          />
        )}
      </div>
    </div>
  );
};

export default ChartViewer;
